/* Public radio (operators): the stations with what is on now, and the DJs with their voices. */
import { A } from '@solidjs/router';
import { For, Show, createResource, createSignal } from 'solid-js';
import { api, DJ, Station } from '../api';
import { ErrorLine, PageHeader, StatusPill, useAction } from '../ui';

const statusTone = { on_air: 'ok', maintenance: 'warn', off: 'off' } as const;
const statusLabel = { on_air: 'On air', maintenance: 'Maintenance', off: 'Off air' } as const;

/** "until 16:00" in the station's own time zone, or nothing when the programme runs all week. */
export function untilLabel(s: Station) {
  if (!s.on_air.until) return 'all week';
  const t = new Date(s.on_air.until + 'Z').toLocaleTimeString('en-GB', { timeZone: s.timezone, hour: '2-digit', minute: '2-digit' });
  return `until ${t}`;
}

function DJRow(props: { dj: DJ; onSaved: () => void }) {
  const [edit, setEdit] = createSignal(false);
  const [f, setF] = createSignal({ display_name: '', bio: '', voice: '' });
  const a = useAction();
  const open = () => { setF({ display_name: props.dj.display_name, bio: props.dj.bio ?? '', voice: props.dj.voice ?? '' }); setEdit(true); };
  const save = (e: Event) => {
    e.preventDefault();
    a.run(async () => {
      await api(`/radio/djs/${props.dj.id}`, { method: 'PATCH',
        json: { display_name: f().display_name, bio: f().bio.trim() || null, voice: f().voice.trim() || null } });
      setEdit(false);
      props.onSaved();
    });
  };
  return (
    <tr class="align-top">
      <td class="font-semibold">{props.dj.display_name}</td>
      <td class="text-ink-2">{props.dj.stations.join(', ') || '—'}</td>
      <td>
        <Show when={props.dj.voice} fallback={<span class="whitespace-nowrap">{props.dj.talks
          ? <StatusPill tone="warn">Not cast yet</StatusPill> : <StatusPill tone="off">Never talks</StatusPill>}</span>}>
          <code class="mono text-xs text-ink-3" title={props.dj.voice!}>{props.dj.voice!.slice(0, 8)}…</code>
        </Show>
      </td>
      <td class="w-full">
        <Show when={edit()} fallback={<div class="flex items-start gap-2"><span class="flex-1 text-sm text-ink-2">{props.dj.bio}</span>
          <button class="btn btn-ghost btn-xs" onClick={open}>Edit</button></div>}>
          <form class="flex flex-col gap-2" onSubmit={save}>
            <label class="form-control"><span class="label-text text-sm font-semibold">Name</span>
              <input class="input input-sm input-bordered" required value={f().display_name} onInput={(e) => setF({ ...f(), display_name: e.currentTarget.value })} /></label>
            <label class="form-control"><span class="label-text text-sm font-semibold">Bio (public)</span>
              <textarea class="textarea textarea-bordered text-sm" rows="2" maxLength={500} value={f().bio} onInput={(e) => setF({ ...f(), bio: e.currentTarget.value })} /></label>
            <label class="form-control"><span class="label-text text-sm font-semibold">ScaiSpeak voice id (empty: not cast yet, or never talks)</span>
              <input class="input input-sm input-bordered mono" value={f().voice} onInput={(e) => setF({ ...f(), voice: e.currentTarget.value })} /></label>
            <div class="flex gap-2">
              <button class="btn btn-sm btn-primary" disabled={a.busy()}>Save</button>
              <button type="button" class="btn btn-sm btn-ghost" onClick={() => setEdit(false)}>Cancel</button>
            </div>
            <ErrorLine error={a.error} />
          </form>
        </Show>
      </td>
    </tr>
  );
}

export default function Radio() {
  const [stations] = createResource(() => api<Station[]>('/radio/stations'));
  const [djs, { refetch }] = createResource(() => api<DJ[]>('/radio/djs'));

  return (
    <>
      <PageHeader title="Radio" />
      <p class="-mt-3 mb-6 max-w-prose text-ink-2">The free public stations on scai.fm, each with one DJ. A station plays its base format
        whenever its weekly grid has no programme on.</p>

      <div class="mb-8 grid gap-4 sm:grid-cols-2">
        <For each={stations()} fallback={<p class="text-ink-3">No stations yet. Run <code class="mono">scaifm radio-seed</code> on be1 for the launch line-up.</p>}>
          {(s) => (
            <A href={`/radio/${s.id}`} class="card-z flex flex-col gap-2 no-underline hover:border-magenta">
              <div class="flex items-center gap-3">
                <h2 class="wordmark text-2xl">{s.name}</h2>
                <span class="ml-auto"><StatusPill tone={statusTone[s.status]}>{statusLabel[s.status]}</StatusPill></span>
              </div>
              <div class="text-sm text-ink-2">{s.dj ? <>with <b>{s.dj.display_name}</b></> : 'Customer station, no host'} · {s.programmes.length} programmes</div>
              <div class="text-sm"><span class="th">Now</span> {s.on_air.name} <span class="text-ink-3">{untilLabel(s)}</span><Show when={s.status === 'on_air'}> · {s.listeners} listening</Show></div>
            </A>
          )}
        </For>
      </div>

      <h2 class="th mb-2">DJs</h2>
      <div class="card-z overflow-x-auto p-0">
        <table class="table">
          <thead><tr><th class="th">DJ</th><th class="th">Station</th><th class="th">Voice</th><th class="th">Bio</th></tr></thead>
          <tbody>
            <For each={djs()}>{(d) => <DJRow dj={d} onSaved={refetch} />}</For>
          </tbody>
        </table>
      </div>
      <p class="mt-2 text-sm text-ink-3">Characters come from <code class="mono">characters/noodle-squad.md</code>. A DJ whose station never talks needs no voice.</p>
    </>
  );
}
